import { CommonModule } from '@angular/common';
import {Component, Injector, Input, OnDestroy, OnInit} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { EventViewModel } from '../../../../model/events.model';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from '../../../../common/common';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { NgxPaginationModule } from 'ngx-pagination';
import { FormsModule } from '@angular/forms';
import { PaginationClass, PaginationModel } from "../../../../common/pagination";
import {BehaviorSubject, Subject} from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {LoaderComponent} from "../../../common/loader/loader.component";

@Component({
  selector: "app-event-index",
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, FormsModule, LoaderComponent],
  templateUrl: "./event-index.component.html",
  styleUrl: "./event-index.component.scss",
})
export class EventIndexComponent implements OnInit, OnDestroy {
  @Input() public isComponentImported: boolean = false;
  @Input() public elementsPerPage: number = 10;

  protected colspan: number = this.isComponentImported ? 8 : 14;
  // Search fields
  public idSearch: string = '';
  public eventIdSearch: string = '';
  public venIdSearch: string = '';
  public startTimeSearch: string = '';
  public durationSearch: string = '';
  public testSearch: string = '';
  public prioritySearch: string = '';
  public toleranceSearch: string = '';
  public acceptedSearch: string = '';
  public marketContextSearch: string = '';
  public groupIdSearch: string = '';
  public resourceIdSearch: string = '';
  public partyIdSearch: string = '';

  // Debounce search
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    eventId: '',
    venId: '',
    startTime: '',
    duration: '',
    test: '',
    priority: '',
    tolerance: '',
    accepted: '',
    marketContext: '',
    groupId: '',
    resourceId: '',
    partyId: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'eventsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public displayedEvents: EventViewModel[] = [];
  public isLoading: boolean = false;
  public error: string | null = null;

  // Sorting
  public sortField: string = '';
  public sortOrder: 'asc' | 'desc' = 'asc';

  public router: Router;

  constructor(private injector: Injector, private eventService: ApiService) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.paginationModel = this.pagination.resetPagination(this.elementsPerPage);

    // Setup debounced search
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.fetchEvents();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  fetchEvents() {
    this.isLoading = true;
    this.error = null;

    const queryParams = this.buildSearchParams();
    this.eventService.getEvents(queryParams).subscribe({
      next: (response: any) => {
        this.displayedEvents = response.map((event: EventViewModel) => event);
        this.paginationModel = this.pagination.updateTotalItems(response.length);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading event:', err);
        this.error = extractErrorMessage(err);
        this.isLoading = false;
      }
    });
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depths': JSON.stringify({
        "groups": 1, //Something is broken when it's 0
        "parties": this.isComponentImported ? 0 : 1,
        "resources": this.isComponentImported ? 0 : 1,
        "signals": this.isComponentImported ? 0 : 1,
        "market_context": this.isComponentImported ? 0 : 1,
      }),
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['id'] = this.idSearch.trim();
    }
    if (this.eventIdSearch) {
      params['event_id'] = this.eventIdSearch.trim();
    }
    if (this.venIdSearch) {
      params['ven_id'] = this.venIdSearch.trim();
    }
    if (this.startTimeSearch) {
      params['dtstart'] = this.startTimeSearch.trim();
    }
    if (this.durationSearch) {
      params['duration'] = this.durationSearch.trim();
    }
    if (this.testSearch) {
      params['test'] = this.testSearch.trim();
    }
    if (this.prioritySearch) {
      params['priority'] = this.prioritySearch.trim();
    }
    if (this.toleranceSearch) {
      params['tolerance'] = this.toleranceSearch.trim();
    }
    if (this.acceptedSearch) {
      params['accepted'] = this.acceptedSearch.trim();
    }

    // Nested filters for related objects
    if (this.marketContextSearch) {
      params['market_context.market_context'] = this.marketContextSearch.trim();
    }
    if (this.groupIdSearch) {
      params['groups.group_id'] = this.groupIdSearch.trim();
    }
    if (this.resourceIdSearch) {
      params['resources.resource_id'] = this.resourceIdSearch.trim();
    }
    if (this.partyIdSearch) {
      params['parties.party_id'] = this.partyIdSearch.trim();
    }

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination(this.elementsPerPage);
    this.searchParams$.next({
      id: this.idSearch,
      eventId: this.eventIdSearch,
      venId: this.venIdSearch,
      startTime: this.startTimeSearch,
      duration: this.durationSearch,
      test: this.testSearch,
      priority: this.prioritySearch,
      tolerance: this.toleranceSearch,
      accepted: this.acceptedSearch,
      marketContext: this.marketContextSearch,
      groupId: this.groupIdSearch,
      resourceId: this.resourceIdSearch,
      partyId: this.partyIdSearch
    });
  }


  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.fetchEvents();
  }

  /**
   * Refresh event - reload current page
   */
  refreshEvents() {
    this.fetchEvents();
  }

  /**
   * Navigation helper
   */
  nextPage(eventId: number, route: string, router: Router) {
    goToNextPage(eventId, route, router);
  }
}
